import React, { useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { Formik } from 'formik'
import { Portal, PortalHost } from '@gorhom/portal'

import { Column, Row } from '@components/kit/grid'
import Text from '@components/kit/text'
import Card from '@components/kit/card'
import Box from '@components/kit/box'
import Button from '@components/kit/button'
import Modal from '@components/kit/modal'
import ScrollView from '@components/kit/scroll-view'
import Form, { RadioGroup } from '@components/kit/form'
import Pressable from '@components/kit/pressable'
import CheckBoxWrapper from '@components/kit/form/inputs/checkbox-input/checkbox-wrapper'
import { ChevronDownIcon } from '@components/kit/icons'
import Loading from '@components/kit/loading'
import DateTimePicker, {
  ImperativeDateTimePickerProps,
  DATE_TIME_PICKER_MAX_HEIGHT
} from '@components/kit/datetime-picker'

import { useMedsStore } from '@store/Meds'
import { useOpportunityStore } from '@store/Opportunity'
import { IAvailableCondition } from '@store/Conditions/types'
import {
  ICLinicalImpactOption,
  IClinicalImpactCosts,
  IClinicalImpactLog,
  IMedicalImpactLog,
  MedicalImpactStatus,
  MedicalImpactType,
  ProviderResponse
} from '@store/Meds/types'
import {
  EventSourceEnum,
  IOpportunityDetails
} from '@store/Opportunity/types'
import { ChatContext } from '../chat.web'
import { useConditionsStore } from '@store/Conditions'
import {
  FormButtonPanel,
  RadioInputFormated,
  SelectFieldFormated,
  SwitchInputFormated,
  TextFieldFormated
} from '@components/formFields'
import {
  ColumnWrapper,
  ValueText,
  RowWrapper,
  LabelText
} from '@components/table'
import { colors } from '@theme'
import FlatList from '@components/kit/flat-list'
import {
  MedicationSearchSelect,
  SelectedMedGpi,
  getSelectedGpis,
  getMedicationSummary
} from '@components/searchMedications/searchMedications'

/* -----------------------------------------------------------------------------
 * CheckBoxItem
 * -------------------------------------------------------------------------- */

interface CheckBoxItemProps {
  label: string
  isLast?: boolean
  onPress: (isSelected: boolean) => void
}

const CheckBoxItem = ({
  label,
  isLast = false,
  onPress
}: CheckBoxItemProps) => {
  const [isSelected, setIsSelected] = useState(false)

  const handleToggle = () => {
    const newSelectedValue = !isSelected
    onPress(newSelectedValue)
    setIsSelected(newSelectedValue)
  }

  return (
    <RowWrapper
      paddingY='08'
      paddingX='04'
      borderBottomWidth={isLast ? '0px' : '1px'}
    >
      <Pressable
        onPress={handleToggle}
        flexDirection='row'
        p='00'
        alignItems='center'
      >
        <CheckBoxWrapper
          isChecked={isSelected}
          size={20}
          children={null}
        />
        <Text
          width='90%'
          textColor='walrusBlue200'
          font='display.14.bold'
          paddingLeft='12'
          lineBreakMode='tail'
        >
          {label}
        </Text>
      </Pressable>
    </RowWrapper>
  )
}

/* -----------------------------------------------------------------------------
 * RadioGroupItem
 * -------------------------------------------------------------------------- */

interface RadioGroupItemProps {
  name: string
  items: { label: string; value: number }[]
  onSelect: (value: number) => void
}

const RadioGroupItem = ({ name, items, onSelect }: RadioGroupItemProps) => {
  const [selectedValue, setSelectedValue] = useState('')

  const handleSelect = (value: string) => {
    if (!isNaN(Number(value))) {
      onSelect(parseInt(value))
    } else {
      onSelect(0)
    }
    setSelectedValue(value)
  }

  return (
    <RadioGroup>
      {items.map((item, index) => (
        <RowWrapper
          key={item.value}
          paddingY='08'
          paddingX='04'
          gap='12'
          borderBottomWidth={index === items.length - 1 ? '0px' : '1px'}
          justifyContent='flex-start'
        >
          <RadioInputFormated
            name={name}
            value={`${item.value}`}
            selectedValue={selectedValue}
            onChange={handleSelect}
          />
          <ValueText>{item.label}</ValueText>
        </RowWrapper>
      ))}
    </RadioGroup>
  )
}

/* -----------------------------------------------------------------------------
 * FormSection
 * -------------------------------------------------------------------------- */

interface FormSectionProps {
  title: string
  isLast?: boolean
  isVertical?: boolean
  contentWidth?: string
  children: React.ReactNode
  [key: string]: any
}

const FormSection = ({
  title,
  children,
  isLast,
  isVertical = false,
  contentWidth = '280px',
  ...otherProps
}: FormSectionProps) => {
  if (isVertical) {
    return (
      <Box
        display='flex'
        flex={1}
        gap='16'
        paddingY='00'
        paddingX='00'
        alignContent='flex-start'
        width='323px'
        // maxWidth='25%'
        borderRightWidth={isLast ? '0px' : '1px'}
        borderColor='shade03'
        {...otherProps}
      >
        <Box m='00' paddingY='00' paddingLeft='00' paddingRight='24'>
          <ValueText font='display.16.bold'>{title}</ValueText>
        </Box>
        {children}
      </Box>
    )
  }
  return (
    <RowWrapper
      gap='16'
      paddingY='24'
      paddingX='00'
      alignContent='flex-start'
      borderBottomWidth={isLast ? '0px' : '1px'}
      {...otherProps}
    >
      <Column width='160px' alignSelf='start'>
        <ValueText>{title}</ValueText>
      </Column>
      <Column width={contentWidth}>{children}</Column>
    </RowWrapper>
  )
}

/* -----------------------------------------------------------------------------
 * CostInputGroup
 * -------------------------------------------------------------------------- */
interface CostInputGroupProps {
  title?: string
  cost?: number
  daysSupply?: number
  onCostChange: (value: number) => void
  onDaysSupplyChange: (value: number) => void
}
const CostInputGroup = ({
  title,
  cost,
  daysSupply,
  onCostChange,
  onDaysSupplyChange
}: CostInputGroupProps) => {
  const [costString, setCostString] = useState(cost?.toString?.() || '')
  const [daysSupplyString, setDaysSupplyString] = useState(
    daysSupply?.toString?.() || ''
  )

  const handleCostChange = (value: string) => {
    if (!value.match(/^[0-9]*[.,]?[0-9]*$/)) {
      return
    }
    if (!isNaN(Number(value))) {
      onCostChange(Number(value))
      setCostString(value)
    } else {
      setCostString(value)
    }
  }

  const handleDaysSupplyChange = (value: string) => {
    if (!value.match(/^[0-9]*$/)) {
      return
    }
    if (!isNaN(Number(value))) {
      onDaysSupplyChange(Number(value))
      setDaysSupplyString(value)
    } else {
      setDaysSupplyString(value)
    }
  }

  return (
    <Box>
      <Text font='display.14.bold' textColor='walrusBlue200' marginBottom='08'>
        {title}
      </Text>
      <Column width='169px' m='00' paddingY='00' flexDirection='row' gap='08'>
        <TextFieldFormated
          width='80px'
          height='90px'
          name='ClientCostsBefore'
          label='Cost $'
          value={costString}
          onChangeText={handleCostChange}
        />
        <TextFieldFormated
          width='80px'
          height='90px'
          name='ClientSupplyBefore'
          label='Days Supply'
          value={daysSupplyString}
          onChangeText={handleDaysSupplyChange}
        />
      </Column>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * NewClinicalImpactModal
 * -------------------------------------------------------------------------- */

interface OpportunityRuleProps {
  item: IOpportunityDetails
  selected: boolean
  onSelect: (item: IOpportunityDetails) => void
}

function OpportunityRuleItem({
  item,
  selected,
  onSelect
}: OpportunityRuleProps) {
  return (
    <Pressable onPress={() => onSelect(item)}>
      <Box
        padding='16'
        marginY='04'
        marginRight='08'
        marginLeft='04'
        // @ts-ignore
        style={{
          borderRadius: '12px',
          borderWidth: '1px',
          borderColor: '#F9F9F9',
          boxShadow: '0px 2px 4px 0px rgba(62, 64, 136, 0.08)'
        }}
        flexDirection='row'
        alignItems='center'
      >
        <CheckBoxWrapper
          isChecked={selected}
          size={20}
          children={null}
        />
        <Text font='display.10.bold' marginLeft='16'>
          {item.rule_name}
        </Text>
      </Box>
    </Pressable>
  )
}

interface CustomNativeEventLayout {
  top: number
  left: number
  width: number
  height: number
}

const getCustomLayoutDimensions = ({ nativeEvent }: { nativeEvent: any }) => {
  const layout = nativeEvent?.layout as CustomNativeEventLayout
  return {
    top: layout.top || 0,
    left: layout.left || 0,
    bottom: (layout.top || 0) + (layout.height || 0),
    right: (layout.left || 0) + (layout.width || 0)
  }
}

interface ClinicalImpactModalProps {
  clinicalOptions: ICLinicalImpactOption[]
  conditionOptions: IAvailableCondition[]
  onRequestClose: () => void
  onRequestSave: () => void
}

function ClinicalImpactModal({
  clinicalOptions,
  conditionOptions,
  onRequestClose,
  onRequestSave
}: ClinicalImpactModalProps) {
  const { userId } = useContext(ChatContext)
  const { searchUserConditionsByName, searchUserClinicalOptionsByName } =
    useMedsStore.getState()
  const { getOpportunitiesByUserId } = useOpportunityStore.getState()
  const [searchedClinicalOptions, setSearchedClinicalOptions] = useState<
    ICLinicalImpactOption[]
  >([])
  const [searchedConditionOptions, setSearchedConditionOptions] = useState<
    IAvailableCondition[]
  >([])
  const [isSearchingConditions, setIsSearchingConditions] = useState(false)
  const [isSearchingClinicalOptions, setIsSearchingClinicalOptions] =
    useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [searchEvent, setSearchEvent] = useState('')
  const [searchCondition, setSearchCondition] = useState('')
  const [costs, setCosts] = useState<IClinicalImpactCosts>({})
  const [selectedMedications, setSelectedMedications] = useState<
    SelectedMedGpi[]
  >([])
  const [clinicalOptionsSelected, setClinicalOptionsSelected] = useState<
    number[]
  >([])
  const [conditionOptionsSelected, setConditionOptionsSelected] = useState<
    number[]
  >([])

  const [preventSearch, setPreventSearch] = useState(false)
  const [lastTimeoutClinical, setLastTimeoutClinical] = useState<number | null>(
    null
  )
  const [lastTimeoutConditions, setLastTimeoutConditions] = useState<
    number | null
  >(null)

  const providerResponseOptions = [
    { label: 'Provider accepted', value: ProviderResponse.Accepted },
    { label: 'Provider rejected', value: ProviderResponse.Rejected },
    { label: 'Provider pending', value: ProviderResponse.Pending }
  ]

  // empty until picked: a default here was saved as the provider's answer on
  // logs with no outreach at all
  const [providerResponse, setProviderResponse] = useState<
    (typeof providerResponseOptions)[number] | null
  >(null)

  const eventSourceOptions = [
    { label: 'Pharmacist Surfaced', value: EventSourceEnum.PharmacistSurfaced },
    { label: 'Member Surfaced', value: EventSourceEnum.MemberSurfaced },
    { label: 'Rules Surfaced', value: EventSourceEnum.RulesSurfaced }
  ]

  const [eventSource, setEventSource] = useState(eventSourceOptions[0])

  const statusOptions = [
    { label: 'Active', value: MedicalImpactStatus.Active },
    { label: 'Confirmed', value: MedicalImpactStatus.Confirmed },
    { label: 'Pending', value: MedicalImpactStatus.Pending }
  ]

  const [status, setStatus] = useState(statusOptions[0])

  const [outerLayoutDimensions, setOuterLayoutDimensions] = useState({
    width: 0,
    height: 0
  })

  const [hasProviderOutreach, setHasProviderOutreach] = useState(false)

  // the response is the only record of an outreach, so one needs it
  const providerResponseMissing = hasProviderOutreach && !providerResponse

  const [opportunityRuleOptions, setOpportunityRuleOptions] = useState<
    IOpportunityDetails[]
  >([])

  const [selectedOpportunityRule, setSelectedOpportunityRule] =
    useState<IOpportunityDetails | null>(null)

  // flipper refuses a rules surfaced event that does not name its rule
  const ruleReferenceMissing =
    eventSource?.value === EventSourceEnum.RulesSurfaced &&
    !selectedOpportunityRule

  const handleAlterClinicalOption = (optionId: number) => {
    const newSelectedOptions = clinicalOptionsSelected.includes(optionId)
      ? clinicalOptionsSelected.filter(id => id !== optionId)
      : [...clinicalOptionsSelected, optionId]
    setClinicalOptionsSelected(newSelectedOptions)
  }

  const handleAlterConditionOption = (optionId: number) => {
    const newSelectedOptions = conditionOptionsSelected.includes(optionId)
      ? conditionOptionsSelected.filter(id => id !== optionId)
      : [...conditionOptionsSelected, optionId]
    setConditionOptionsSelected(newSelectedOptions)
  }

  const delayedSearchTrigger = (
    content: string,
    setValue: (value: string) => void,
    searchContent: (content: string) => Promise<void>,
    setIsSearchingContent: (isSearching: boolean) => void,
    lastTimeout: number | null = null,
    setLastTimeout: (timeout: number | null) => void
  ) => {
    if (preventSearch) {
      return
    }
    if (content === '') {
      setValue('')
      setIsSearchingContent(false)
      lastTimeout && clearTimeout(lastTimeout)
      setLastTimeout(null)
      return
    }
    setValue(content)
    if (lastTimeout) {
      setIsSearchingContent(true)
      clearTimeout(lastTimeout)
    }
    const typingTimeOut = window.setTimeout(() => {
      setIsSearchingContent(true)
      setPreventSearch(true) // maybe this will cause a glitch, remove if necessary
      searchContent(content).finally(() => {
        clearTimeout(lastTimeout)
        setPreventSearch(false)
        setIsSearchingContent(false)
        setLastTimeout(null)
      })
    }, 2000)
    setLastTimeout(typingTimeOut)
  }

  const searchConditions = async (name: string) => {
    const conditions = await searchUserConditionsByName(userId, name)
    setSearchedConditionOptions(conditions as IAvailableCondition[])
  }

  const searchClinicalOptions = async (name: string) => {
    const clinicals = await searchUserClinicalOptionsByName(userId, name)
    setSearchedClinicalOptions(clinicals)
  }

  const onSave = () => {
    if (providerResponseMissing) {
      return
    }
    if (clinicalOptionsSelected.length === 0) {
      return
    }
    if (ruleReferenceMissing) {
      return
    }
    setIsSubmitting(true)
    setSaveError('')
    const opportunityRuleId = parseInt(selectedOpportunityRule?.id)
    const gpis = getSelectedGpis(selectedMedications)
    const medication = getMedicationSummary(selectedMedications)
    useMedsStore
      .getState()
      .addClinicalImpactLog(
        userId,
        conditionOptionsSelected,
        clinicalOptionsSelected,
        medication || undefined,
        gpis.length ? gpis : undefined,
        status.value,
        costs,
        hasProviderOutreach ? providerResponse?.value : undefined,
        eventSource?.value,
        isNaN(opportunityRuleId) ? undefined : opportunityRuleId
      )
      .then(result => {
        // on failure the modal stays open with the form still filled in, so
        // saving again is one click instead of logging it all over
        if (!result.success) {
          setSaveError(result.errorMessage)
          return
        }
        onRequestSave()
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const onCancel = () => {
    onRequestClose()
  }

  const getCustomLayoutDimensions = ({ nativeEvent }: { nativeEvent: any }) => {
    const layout = nativeEvent?.layout as any as CustomNativeEventLayout
    setOuterLayoutDimensions({
      width: layout.width,
      height: layout.height
    })
  }

  useEffect(() => {
    getOpportunitiesByUserId(userId).then(opportunities => {
      setOpportunityRuleOptions(opportunities)
    })
  }, [userId, getOpportunitiesByUserId])

  return (
    <Box minWidth='544px' maxHeight='85vh' maxWidth='85vw'>
      <Text font='display.20.bold' textAlign='left' marginBottom='16'>
        Log Clinical Impact
      </Text>
      <ScrollView
        onLayout={getCustomLayoutDimensions}
        style={{ flex: 1 }}
        contentContainerStyle={{ flexShrink: 1 }}
        horizontal
      >
        <Formik initialValues={{}} onSubmit={() => {}} validateOnChange={true}>
          <Form>
            <Box m='00' p='00' flexDirection='row' alignItems='flex-start'>
              <FormSection
                title='Event *'
                isVertical
                height={outerLayoutDimensions.height || '100%'}
              >
                <Box m='00' paddingY='00' paddingRight='24'>
                  <TextFieldFormated
                    label='Quick search'
                    name='Event Search'
                    value={searchEvent}
                    onChangeText={text =>
                      delayedSearchTrigger(
                        text,
                        setSearchEvent,
                        searchClinicalOptions,
                        setIsSearchingClinicalOptions,
                        lastTimeoutClinical,
                        setLastTimeoutClinical
                      )
                    }
                    width='100%'
                  />
                </Box>
                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ flexShrink: 1 }}
                  showsVerticalScrollIndicator={false}
                  marginBottom='24'
                >
                  <Box m='00' paddingY='00' paddingRight='24'>
                    {isSearchingClinicalOptions ? (
                      <Box paddingTop='24'>
                        <Loading size='small' />
                      </Box>
                    ) : (
                      <FlatList
                        showsVerticalScrollIndicator={false}
                        onEndReachedThreshold={0.5}
                        data={
                          searchedClinicalOptions.length > 0 &&
                          searchEvent !== ''
                            ? searchedClinicalOptions
                            : clinicalOptions
                        }
                        maxToRenderPerBatch={10}
                        // @ts-ignore
                        renderItem={({
                          item,
                          index
                        }: {
                          item: ICLinicalImpactOption
                          index: number
                        }) => (
                          <CheckBoxItem
                            label={item.name}
                            onPress={() => handleAlterClinicalOption(item.id)}
                            isLast={
                              searchedClinicalOptions.length > 0 &&
                              searchEvent !== ''
                                ? index === searchedClinicalOptions.length - 1
                                : index === clinicalOptions.length - 1
                            }
                          />
                        )}
                        keyExtractor={(item: ICLinicalImpactOption) =>
                          item.id.toString()
                        }
                      />
                    )}
                  </Box>
                </ScrollView>
              </FormSection>
              <FormSection
                title='Condition'
                isVertical
                height={outerLayoutDimensions.height || '100%'}
                marginLeft='24'
              >
                <Box m='00' paddingY='00' paddingRight='24'>
                  <TextFieldFormated
                    label='Quick search'
                    name='Condition Search'
                    value={searchCondition}
                    onChangeText={text =>
                      delayedSearchTrigger(
                        text,
                        setSearchCondition,
                        searchConditions,
                        setIsSearchingConditions,
                        lastTimeoutConditions,
                        setLastTimeoutConditions
                      )
                    }
                    width='100%'
                  />
                </Box>
                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ flexShrink: 1 }}
                  showsVerticalScrollIndicator={false}
                  marginBottom='24'
                >
                  <Box m='00' paddingY='00' paddingRight='24'>
                    {isSearchingConditions ? (
                      <Box paddingTop='24'>
                        <Loading size='small' />
                      </Box>
                    ) : (
                      <FlatList
                        showsVerticalScrollIndicator={false}
                        onEndReachedThreshold={0.5}
                        data={
                          searchedConditionOptions.length > 0 &&
                          searchCondition !== ''
                            ? searchedConditionOptions
                            : conditionOptions
                        }
                        maxToRenderPerBatch={10}
                        // @ts-ignore
                        renderItem={({
                          item,
                          index
                        }: {
                          item: IAvailableCondition
                          index: number
                        }) => (
                          <CheckBoxItem
                            label={item.name}
                            onPress={() => handleAlterConditionOption(item.id)}
                            isLast={
                              searchedConditionOptions.length > 0 &&
                              searchCondition !== ''
                                ? index === searchedConditionOptions.length - 1
                                : index === conditionOptions.length - 1
                            }
                          />
                        )}
                        keyExtractor={(item: IAvailableCondition) =>
                          item.id.toString()
                        }
                      />
                    )}
                  </Box>
                </ScrollView>
              </FormSection>
              <FormSection
                title='Medications'
                marginX='24'
                height={outerLayoutDimensions.height || '100%'}
                width='371px'
                isVertical
              >
                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ flexShrink: 1 }}
                  showsVerticalScrollIndicator={false}
                  marginBottom='24'
                >
                  <Box m='00' paddingY='00' paddingRight='24'>
                    <MedicationSearchSelect
                      selectedItems={selectedMedications}
                      onSelectionChange={setSelectedMedications}
                      label='Quick Search'
                      minHeight='160px'
                      maxHeight='220px'
                      showsScrollIndicator={true}
                    />
                    <Box m='00' paddingY='24'>
                      <ValueText font='display.16.bold'>Costs</ValueText>
                    </Box>
                    <Box style={{ flexGrow: 1 }} gap='08'>
                      <Row style={{ flexShrink: 1 }} gap='08'>
                        <CostInputGroup
                          title='Client costs before'
                          cost={costs.client_before_cost}
                          daysSupply={costs.client_before_days}
                          onCostChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              client_before_cost: value
                            }))
                          }
                          onDaysSupplyChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              client_before_days: value
                            }))
                          }
                        />
                        <CostInputGroup
                          title='Client costs after'
                          cost={costs.client_after_cost}
                          daysSupply={costs.client_after_days}
                          onCostChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              client_after_cost: value
                            }))
                          }
                          onDaysSupplyChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              client_after_days: value
                            }))
                          }
                        />
                      </Row>
                      <Row style={{ flexShrink: 1 }} gap='08'>
                        <CostInputGroup
                          title='Member costs before'
                          cost={costs.member_before_cost}
                          daysSupply={costs.member_before_days}
                          onCostChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              member_before_cost: value
                            }))
                          }
                          onDaysSupplyChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              member_before_days: value
                            }))
                          }
                        />
                        <CostInputGroup
                          title='Member costs after'
                          cost={costs.member_after_cost}
                          daysSupply={costs.member_after_days}
                          onCostChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              member_after_cost: value
                            }))
                          }
                          onDaysSupplyChange={value =>
                            setCosts(prev => ({
                              ...prev,
                              member_after_days: value
                            }))
                          }
                        />
                      </Row>
                    </Box>
                  </Box>
                </ScrollView>
              </FormSection>

              <FormSection
                title='Provider outreach'
                marginX='24'
                height={outerLayoutDimensions.height || '100%'}
                width='347px'
                isVertical
                isLast
              >
                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ flexShrink: 1 }}
                  showsVerticalScrollIndicator={false}
                  marginBottom='24'
                >
                  <Box flexDirection='column-reverse'>
                    <Box
                      style={{ flex: 1 }}
                      marginBottom='24'
                      minHeight='300px'
                    >
                      <FlatList
                        showsVerticalScrollIndicator={false}
                        onEndReachedThreshold={0.5}
                        data={opportunityRuleOptions}
                        maxToRenderPerBatch={10}
                        // @ts-ignore
                        renderItem={({
                          item
                        }: {
                          item: IOpportunityDetails
                        }) => (
                          <OpportunityRuleItem
                            item={item}
                            selected={selectedOpportunityRule?.id === item.id}
                            onSelect={setSelectedOpportunityRule}
                          />
                        )}
                        keyExtractor={(item: IOpportunityDetails) =>
                          item.id.toString()
                        }
                      />
                    </Box>
                    {ruleReferenceMissing && (
                      <Text
                        font='display.12.bold'
                        textColor='red'
                        marginBottom='12'
                      >
                        Please select the rule that surfaced this event
                      </Text>
                    )}
                    <Text
                      font='display.14.bold'
                      textColor={'walrusBlue700'}
                      paddingY='24'
                    >
                      Rule reference
                    </Text>
                    <Box
                      flexDirection='column'
                      gap='00'
                      backgroundColor='walrusBlue050'
                      width='100%'
                    >
                      <Text
                        paddingTop='12'
                        paddingLeft='12'
                        font='display.12.bold'
                        textColor={'walrusBlue700'}
                      >
                        Event source
                      </Text>
                      <SelectFieldFormated
                        name='Event source'
                        options={eventSourceOptions}
                        value={eventSource}
                        onChange={setEventSource}
                        styles={{
                          control: {
                            backgroundColor: colors.walrusBlue050
                          },
                          option: {
                            backgroundColor: colors.white
                          }
                        }}
                      />
                    </Box>
                    <Box m='00' paddingY='24'>
                      <ValueText font='display.16.bold'>Event source</ValueText>
                    </Box>
                    {providerResponseMissing && (
                      <Text
                        font='display.12.bold'
                        textColor='red'
                        marginTop='08'
                      >
                        Please select the provider response
                      </Text>
                    )}
                    <Box
                      flexDirection='column'
                      gap='00'
                      backgroundColor='walrusBlue050'
                      width='100%'
                      marginTop='16'
                    >
                      <Text
                        paddingTop='12'
                        paddingLeft='12'
                        font='display.12.bold'
                        textColor={
                          !hasProviderOutreach
                            ? ('#6670BC' as any)
                            : ('walrusBlue700' as any)
                        }
                      >
                        Provider response
                      </Text>
                      <SelectFieldFormated
                        disabled={!hasProviderOutreach}
                        name='Provider response'
                        options={providerResponseOptions}
                        value={hasProviderOutreach ? providerResponse : null}
                        // @ts-ignore
                        placeholder={
                          !hasProviderOutreach
                            ? 'N/A'
                            : 'Select provider response'
                        }
                        onChange={setProviderResponse}
                        styles={{
                          control: {
                            // color: hasProviderOutreach
                            //   ? colors.walrusBlue700
                            //   : colors.coolGray,

                            backgroundColor: colors.walrusBlue050
                          },
                          option: {
                            backgroundColor: colors.white
                          },
                          singleValue: {
                            color: hasProviderOutreach
                              ? colors.walrusBlue700
                              : colors.coolGray
                          }
                        }}
                      />
                    </Box>
                    <SwitchInputFormated
                      label='Provider outreach'
                      onChange={setHasProviderOutreach}
                      checked={hasProviderOutreach}
                    />
                  </Box>
                </ScrollView>
              </FormSection>
            </Box>
          </Form>
        </Formik>
      </ScrollView>
      <Box
        width='100%'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        paddingBottom='32'
      >
        <Box m='00' p='00' minWidth='323px' maxWidth='25%' paddingTop='20'>
          <Box
            flexDirection='column'
            gap='00'
            backgroundColor='walrusBlue050'
            width='100%'
          >
            <Text
              paddingTop='12'
              paddingLeft='12'
              font='display.12.bold'
              textColor={'walrusBlue700'}
            >
              Status
            </Text>
            <SelectFieldFormated
              name='status'
              options={statusOptions}
              value={status}
              defaultValue={statusOptions[0]}
              onChange={setStatus}
              styles={{
                control: {
                  backgroundColor: colors.walrusBlue050
                },
                option: {
                  backgroundColor: colors.white
                }
              }}
            />
          </Box>
        </Box>
        <Box m='00' p='00' alignItems='flex-end'>
          {!!saveError && (
            <Text
              font='display.12.bold'
              textColor='red'
              textAlign='right'
              paddingTop='20'
            >
              {saveError}
            </Text>
          )}
          <FormButtonPanel
            onCancel={onCancel}
            onSave={onSave}
            fullWidth={false}
          />
        </Box>
      </Box>

      {isSubmitting && (
        <Box
          position='absolute'
          width='100%'
          height='100%'
          top='0'
          left='0'
          right='0'
          flex='1'
          justifyContent='center'
          alignItems='center'
          style={{
            backgroundColor: '#f4f7facc'
          }}
        >
          <Loading size='large' />
        </Box>
      )}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * MedChangeModal
 * -------------------------------------------------------------------------- */

interface MedChangeModalProps {
  onRequestClose: () => void
  onRequestSave: () => void
}

function MedChangeModal({
  onRequestClose,
  onRequestSave
}: MedChangeModalProps) {
  const { userId } = useContext(ChatContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [selectedMedications, setSelectedMedications] = useState<
    SelectedMedGpi[]
  >([])
  const [type, setType] = useState<MedicalImpactType | 0>(0)
  const [hasConversation, setHasConversation] = useState(false)
  const [conversationDate, setConversationDate] = useState('')
  const conversationDateRef = useRef<ImperativeDateTimePickerProps>(null)
  const conversationDateFieldRef = useRef<any>(null)
  const [openPickerAbove, setOpenPickerAbove] = useState(false)
  const [modalRootPosition, setModalRootPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  })
  const [conversationDatePickerPosition, setConversationDatePickerPosition] =
    useState({
      top: 0,
      left: 0,
      bottom: 0,
      right: 0
    })

  const conversationDateMissing = hasConversation && !conversationDate

  const typeOptions = [
    // { label: 'Interchange', value: MedicalImpactType.Interchange },
    { label: 'Brand to Generic', value: MedicalImpactType.BrandToGeneric },
    {
      label: 'Interchange within Same Drug Class',
      value: MedicalImpactType.InterchangeWithinSameDrugClass
    },
    {
      label: 'Interchange to Different Drug Class',
      value: MedicalImpactType.InterchangeToDifferentDrugClass
    },
    {
      label: 'Mark Cubans Cost Plus Pharmacy',
      value: MedicalImpactType.MarkCubansCostPlusPharmacy
    },
    { label: 'Copay Card', value: MedicalImpactType.CopayCard },
    { label: 'Change Days Supply', value: MedicalImpactType.ChangeDaysSupply },
    { label: 'Deprescribe', value: MedicalImpactType.Deprescribe },
    { label: 'Avoided Drug', value: MedicalImpactType.AvoidedDrug },
    {
      label: 'High Risk Drug-Interaction',
      value: MedicalImpactType.HighRiskDrugInteraction
    },
    { label: 'Transfer Pharmacy', value: MedicalImpactType.TransferPharmacy }
  ]

  const statusOptions = [
    { label: 'Active', value: MedicalImpactStatus.Active },
    { label: 'Confirmed', value: MedicalImpactStatus.Confirmed },
    { label: 'Pending', value: MedicalImpactStatus.Pending }
  ]

  const [status, setStatus] = useState(statusOptions[0])

  const handleToggleConversation = (checked: boolean) => {
    setHasConversation(checked)
    if (!checked) {
      setConversationDate('')
    }
  }

  const handleSetConversationDate = (date: moment.Moment) => {
    setConversationDate(date.format('MM/DD/YYYY'))
    setHasConversation(true)
  }

  const handleChangeConversationDate = (text: string) => {
    setConversationDate(text)
  }

  const openConversationDatePicker = () => {
    const presentPicker = () => {
      conversationDateRef?.current?.present()
    }

    if (!conversationDateFieldRef.current?.measureInWindow) {
      setOpenPickerAbove(false)
      presentPicker()
      return
    }

    conversationDateFieldRef.current.measureInWindow(
      (_x: number, y: number, _w: number, height: number) => {
        const viewportHeight =
          typeof window !== 'undefined' ? window.innerHeight : 0
        const wouldOverflowBelow =
          y + height + DATE_TIME_PICKER_MAX_HEIGHT > viewportHeight

        setOpenPickerAbove(wouldOverflowBelow)
        presentPicker()
      }
    )
  }

  const onSave = () => {
    if (type === 0) {
      return
    }
    if (conversationDateMissing) {
      return
    }
    setIsSubmitting(true)
    setSaveError('')
    const gpis = getSelectedGpis(selectedMedications)
    const medication = getMedicationSummary(selectedMedications)
    const formattedConversationDate = conversationDate
      ? moment(conversationDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
      : undefined
    useMedsStore
      .getState()
      .addMedicalImpactLog(
        userId,
        type,
        status.value,
        medication || undefined,
        gpis.length ? gpis : undefined,
        formattedConversationDate
      )
      .then(result => {
        if (!result.success) {
          setSaveError(result.errorMessage)
          return
        }
        onRequestSave()
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const onCancel = () => {
    onRequestClose()
  }

  return (
    <Box minWidth='680px' maxHeight='85vh' flexDirection='column'>
      <Box
        flexDirection='column'
        style={{ flex: 1, overflow: 'hidden' }}
        onLayout={({ nativeEvent }) =>
          setModalRootPosition(getCustomLayoutDimensions({ nativeEvent }))
        }
      >
        <Text font='display.20.bold' textAlign='left' marginBottom='16'>
          Log Med Interchange
        </Text>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={true}
        >
          <Formik
            initialValues={{}}
            onSubmit={() => {}}
            validateOnChange={true}
          >
            <Form>
              <FormSection title='Type of Change *' contentWidth='380px'>
                <RadioGroupItem
                  name='type'
                  items={typeOptions}
                  onSelect={setType}
                />
              </FormSection>
              <FormSection title='Medications' contentWidth='380px'>
                <MedicationSearchSelect
                  selectedItems={selectedMedications}
                  onSelectionChange={setSelectedMedications}
                  label='Quick Search'
                  minHeight='160px'
                  maxHeight='240px'
                  showsScrollIndicator={true}
                />
              </FormSection>
              <Box flexDirection='column-reverse'>
                <FormSection title='Status *' isLast contentWidth='380px'>
                  <Box m='00' p='00' width='fit-content'>
                    <SelectFieldFormated
                      name='status'
                      options={statusOptions}
                      value={status}
                      defaultValue={statusOptions[0]}
                      onChange={setStatus}
                    />
                  </Box>
                </FormSection>
                <FormSection
                  title='Conversation reference'
                  contentWidth='380px'
                >
                  <Pressable
                    onPress={() => handleToggleConversation(!hasConversation)}
                  >
                    <Row alignItems='center' gap='08' marginBottom='12'>
                      <CheckBoxWrapper
                        isChecked={hasConversation}
                        size={20}
                        children={null}
                      />
                      <Text font='text.12.bold' textColor='walrusBlue700'>
                        There was a conversation
                      </Text>
                    </Row>
                  </Pressable>
                  <Pressable
                    ref={conversationDateFieldRef}
                    onPress={openConversationDatePicker}
                  >
                    <TextFieldFormated
                      name='conversationDate'
                      label={null}
                      value={conversationDate}
                      valuePlaceholder='MM/DD/YYYY'
                      onChangeText={handleChangeConversationDate}
                      onLayout={({ nativeEvent }) =>
                        setConversationDatePickerPosition(
                          getCustomLayoutDimensions({ nativeEvent })
                        )
                      }
                    />
                  </Pressable>
                  {conversationDateMissing && (
                    <Text font='display.12.bold' textColor='red' marginTop='04'>
                      Please fill out the conversation date
                    </Text>
                  )}
                  <Portal hostName='medChangeHost'>
                    <DateTimePicker
                      position='absolute'
                      {...(openPickerAbove
                        ? {
                            bottom:
                              modalRootPosition.bottom -
                              conversationDatePickerPosition.top
                          }
                        : {
                            top:
                              conversationDatePickerPosition.bottom -
                              modalRootPosition.top
                          })}
                      left={
                        conversationDatePickerPosition.right -
                        modalRootPosition.left +
                        20
                      }
                      onSubmit={handleSetConversationDate}
                      ref={conversationDateRef}
                      maxDate={moment()}
                      zIndex={99999}
                      hostName='medChangeOverlayHost'
                    />
                  </Portal>
                </FormSection>
              </Box>
            </Form>
          </Formik>
        </ScrollView>

        {!!saveError && (
          <Text
            font='display.12.bold'
            textColor='red'
            textAlign='right'
            paddingTop='20'
          >
            {saveError}
          </Text>
        )}
        <FormButtonPanel onCancel={onCancel} onSave={onSave} />
      </Box>

      {isSubmitting && (
        <Box
          position='absolute'
          width='100%'
          height='100%'
          top='0'
          left='0'
          right='0'
          flex='1'
          justifyContent='center'
          alignItems='center'
          style={{
            backgroundColor: '#f4f7facc'
          }}
        >
          <Loading size='large' />
        </Box>
      )}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * ClinicalImpactLog
 * -------------------------------------------------------------------------- */

interface ClinicalImpactLogProps {
  clinicalImpactLog: IClinicalImpactLog
  clinicalOptions: ICLinicalImpactOption[]
  conditionsOptions: IAvailableCondition[]
}

const ClinicalImpactLog = ({
  clinicalImpactLog,
  clinicalOptions,
  conditionsOptions
}: ClinicalImpactLogProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const getClinicalOptionName = (optionId: number) => {
    const option = clinicalOptions?.find(option => option.id === optionId)
    return option?.name || ''
  }

  const loggedClinicalOptions = clinicalImpactLog.options?.map(option => {
    return getClinicalOptionName(option.option_id)
  })

  const getConditionOptionName = (conditionId: number) => {
    const condition = conditionsOptions?.find(
      condition => condition.id === conditionId
    )
    return condition?.name || ''
  }

  const loggedConditionsOptions = clinicalImpactLog.conditions
    ?.split(',')
    .map(id => {
      return getConditionOptionName(parseInt(id))
    })

  const medicationsDisplay = clinicalImpactLog?.medication_details?.length
    ? clinicalImpactLog.medication_details.join(', ')
    : clinicalImpactLog?.associated_medication || ''

  // the right column holds whichever of the two the log has
  const isSingleColumn =
    !medicationsDisplay && !clinicalImpactLog?.conditions?.length

  if (!clinicalImpactLog || !loggedClinicalOptions) {
    return null
  }

  return (
    <Box p='00'>
      <Row width='100%' p='12' justifyContent={'space-between'}>
        <ValueText>
          {loggedClinicalOptions?.[0]}
          {loggedClinicalOptions?.length > 1 && '...'}
        </ValueText>
        <Pressable
          onPress={() => setIsExpanded(!isExpanded)}
          backgroundColor='shade03b'
          borderRadius='xlarge'
          width='20'
          height='20'
          justifyContent='center'
          alignItems='center'
          p='04'
          style={{ transform: [{ rotate: !isExpanded ? '0deg' : '90deg' }] }}
        >
          <ChevronDownIcon fill='walrusBlue500' width={12} height={12} />
        </Pressable>
      </Row>
      {isExpanded && (
        <>
          {loggedClinicalOptions.length > 1 && (
            <Row px='12' pt='00' pb='12' mt='-08'>
              <Text font='display.12.bold' textColor='newPurple'>
                {loggedClinicalOptions.slice(1).join(', ')}
              </Text>
            </Row>
          )}
          <Row width='100%'>
            <ColumnWrapper width={isSingleColumn ? '100%' : '35%'}>
              <LabelText>
                {moment(clinicalImpactLog._created).format(
                  'MM/DD/YYYY - hh:mm A'
                )}
              </LabelText>
            </ColumnWrapper>
            {!isSingleColumn && (
              <ColumnWrapper width='65%' alignItems='flex-end' gap='04'>
                {clinicalImpactLog.conditions?.length ? (
                  <LabelText textAlign='right'>
                    Condition{loggedConditionsOptions.length > 1 && 's'}:{' '}
                    {loggedConditionsOptions.join(', ')}
                  </LabelText>
                ) : null}
                {medicationsDisplay && (
                  <LabelText textAlign='right'>
                    Meds: {medicationsDisplay}
                  </LabelText>
                )}
              </ColumnWrapper>
            )}
          </Row>
        </>
      )}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * MedicalImpactLog
 * -------------------------------------------------------------------------- */

const MedicalImpactStatusBadge = ({
  status
}: {
  status: MedicalImpactStatus
}) => {
  let color = ''
  switch (status) {
    case MedicalImpactStatus.Active:
      color = 'pink'
      break
    case MedicalImpactStatus.Pending:
      color = 'yellow'
      break
    case MedicalImpactStatus.Confirmed:
      color = 'paleGreen'
      break
    default:
      color = 'shade04'
  }
  return (
    <Box backgroundColor={color as any} borderRadius='small' p='00' my='04'>
      <LabelText py='02' px='08' font='text.12.bold' textColor='white'>
        {MedicalImpactStatus[status]}
      </LabelText>
    </Box>
  )
}

interface MedicalImpactLogProps {
  medicalImpactLog: IMedicalImpactLog
}

const MedicalImpactLog = ({ medicalImpactLog }: MedicalImpactLogProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const getMedicalImpactTypeName = (type: MedicalImpactType) => {
    switch (type) {
      case MedicalImpactType.Interchange:
        return 'Interchange'
      case MedicalImpactType.Deprescribe:
        return 'Deprescribe'
      case MedicalImpactType.AvoidedDrug:
        return 'Avoided Drug'
      case MedicalImpactType.ChangeDaysSupply:
        return 'Change Days Supply'
      case MedicalImpactType.TransferPharmacy:
        return 'Transfer Pharmacy'
      case MedicalImpactType.CopayCard:
        return 'Copay Card'
      case MedicalImpactType.BrandToGeneric:
        return 'Brand to Generic'
      case MedicalImpactType.InterchangeWithinSameDrugClass:
        return 'Interchange within Same Drug Class'
      case MedicalImpactType.InterchangeToDifferentDrugClass:
        return 'Interchange to Different Drug Class'
      case MedicalImpactType.MarkCubansCostPlusPharmacy:
        return 'Mark Cubans Cost Plus Pharmacy'
      case MedicalImpactType.HighRiskDrugInteraction:
        return 'High Risk Drug-Interaction'
      default:
        return ''
    }
  }

  const medicationsDisplay = medicalImpactLog?.medication_details?.length
    ? medicalImpactLog.medication_details.join(', ')
    : medicalImpactLog?.medications || ''

  const isSingleColumn = !medicationsDisplay

  if (
    !medicalImpactLog ||
    !medicalImpactLog?.type_change ||
    !medicalImpactLog?.status
  ) {
    return null
  }

  return (
    <Box p='00'>
      <Row width='100%' p='12' justifyContent={'space-between'}>
        <ValueText>
          {getMedicalImpactTypeName(medicalImpactLog?.type_change)}
        </ValueText>
        <Box p='00' m='00' flexDirection='row' gap='12' alignItems='center'>
          <MedicalImpactStatusBadge status={medicalImpactLog?.status} />
          <Pressable
            onPress={() => setIsExpanded(!isExpanded)}
            backgroundColor='shade03b'
            borderRadius='xlarge'
            width='20'
            height='20'
            justifyContent='center'
            alignItems='center'
            p='04'
            style={{
              flexShrink: 1,
              maxHeight: '20px',
              maxWidth: '20px',
              transform: [{ rotate: isExpanded ? '0deg' : '90deg' }]
            }}
          >
            <ChevronDownIcon fill='walrusBlue100' width={12} height={12} />
          </Pressable>
        </Box>
      </Row>
      {isExpanded && (
        <Row width='100%'>
          <ColumnWrapper width={isSingleColumn ? '100%' : '35%'}>
            <LabelText>
              {moment(medicalImpactLog?._created).format(
                'MM/DD/YYYY - hh:mm A'
              )}
            </LabelText>
          </ColumnWrapper>
          {!isSingleColumn && (
            <ColumnWrapper width='65%' alignItems='flex-end' gap='04'>
              {medicationsDisplay && (
                <LabelText textAlign='right'>
                  Meds: {medicationsDisplay}
                </LabelText>
              )}
            </ColumnWrapper>
          )}
        </Row>
      )}
    </Box>
  )
}

enum LogModalType {
  ClinicalImpact = 'ClinicalImpact',
  MedChange = 'MedChange'
}

/* -----------------------------------------------------------------------------
 * ClinicalImpactLogging
 * -------------------------------------------------------------------------- */

function ClinicalImpactLogging() {
  const { userId } = useContext(ChatContext)
  // This is a workaround to force a reload of the logs, regardless of the actual value (true/false), feel free to change it if needed
  const [reload, setReload] = useState(false)
  const [clinicalOptions, setClinicalOptions] = useState([])
  const [memberClinicalImpactLog, setMemberClinicalImpactLog] = useState<
    IClinicalImpactLog[]
  >([])
  const [memberMedChangeLog, setMemberMedChangeLog] = useState<
    IMedicalImpactLog[]
  >([])
  const [memberLogs, setMemberLogs] = useState<
    { [key: string]: any; _created: string }[]
  >([])
  const [currentModal, setCurrentModal] = useState<LogModalType | null>(null)
  const { getAvailableConditions } = useConditionsStore.getState()
  const [conditionsAvailable, setConditionsAvailable] = useState<
    IAvailableCondition[]
  >([])
  const [isConditionsAvailableLoading, setIsConditionsAvailableLoading] =
    useState<boolean>(true)

  useEffect(() => {
    setIsConditionsAvailableLoading(true)
    getAvailableConditions(userId).then(conditions => {
      setConditionsAvailable(conditions)
      setIsConditionsAvailableLoading(false)
    })
  }, [getAvailableConditions, userId])

  const openClinicalImpactModal = () => {
    setCurrentModal(LogModalType.ClinicalImpact)
  }

  const openLogMedChangeModal = () => {
    setCurrentModal(LogModalType.MedChange)
  }

  const closeLogModal = () => {
    setCurrentModal(null)
  }

  const closeModalAndReloadLogs = () => {
    closeLogModal()
    setReload(!reload)
  }

  const {
    getClinicalImpactOptions,
    getMemberClinicalImpactLogs,
    getMemberMedicalImpactLogs
  } = useMedsStore.getState()

  useEffect(() => {
    const fetchClinicalImpactOptions = async () => {
      const options = await getClinicalImpactOptions(userId)
      if (options) {
        setClinicalOptions(options)
      }
    }

    const fetchMemberClinicalImpactLogs = async () => {
      const logs = await getMemberClinicalImpactLogs(userId)
      if (logs) {
        setMemberClinicalImpactLog(logs)
      }
    }

    const fetchMemberMedicalImpactLogs = async () => {
      const logs = await getMemberMedicalImpactLogs(userId)
      if (logs) {
        setMemberMedChangeLog(logs)
      }
    }

    fetchClinicalImpactOptions()
    fetchMemberClinicalImpactLogs()
    fetchMemberMedicalImpactLogs()
  }, [userId, reload])

  useEffect(() => {
    setMemberLogs(
      [...memberClinicalImpactLog, ...memberMedChangeLog].sort((a, b) => {
        return new Date(b._created).getTime() - new Date(a._created).getTime()
      })
    )
  }, [memberClinicalImpactLog, memberMedChangeLog])

  return (
    <React.Fragment>
      <Row pt='24'>
        <Text ml='12' mb='08' font='display.16.bold'>
          Clinical Impact Logging
        </Text>
      </Row>
      <Box mt='08' flexDirection='row' gap='04'>
        <Button onPress={openClinicalImpactModal} size='fit' variant='tertiary'>
          Log clinical impact
        </Button>
        <Button onPress={openLogMedChangeModal} size='fit' variant='tertiary'>
          Log med change
        </Button>
      </Box>
      <Card mt='08' px='00' py='00'>
        {memberLogs.length === 0 && (
          <RowWrapper>
            <LabelText>No clinical work logs have been recorded yet</LabelText>
          </RowWrapper>
        )}
        {memberLogs.map((log, index) => (
          <>
            {log?.conditions !== undefined || log?.options !== undefined ? (
              <ClinicalImpactLog
                key={index}
                clinicalImpactLog={log as IClinicalImpactLog}
                clinicalOptions={clinicalOptions}
                conditionsOptions={conditionsAvailable}
              />
            ) : (
              <MedicalImpactLog
                key={index}
                medicalImpactLog={log as IMedicalImpactLog}
              />
            )}
          </>
        ))}
      </Card>
      <Modal
        open={currentModal !== null}
        showCloseButton={true}
        onRequestClose={closeLogModal}
      >
        {currentModal === LogModalType.MedChange && (
          <MedChangeModal
            onRequestClose={closeLogModal}
            onRequestSave={closeModalAndReloadLogs}
          />
        )}
        <PortalHost name='medChangeOverlayHost' />
        <PortalHost name='medChangeHost' />
        {currentModal === LogModalType.ClinicalImpact && (
          <ClinicalImpactModal
            clinicalOptions={clinicalOptions}
            conditionOptions={conditionsAvailable}
            onRequestSave={closeModalAndReloadLogs}
            onRequestClose={closeLogModal}
          />
        )}
      </Modal>
    </React.Fragment>
  )
}

export default ClinicalImpactLogging
